import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import JobFilters from "./JobFilters";
import JobList from "./JobList";
import Pagination from "./Pagination";
import { fetchAllJobs } from "../../apis/JobApis";
import { useToastHelpers } from "../Toasts/ToastUtils";
import { useTheme } from "../Theme/ThemeContext";
import { SkeletonJobList } from "../Skeletons";
import SeoMeta from "../SeoMeta";
import {
  CapgeminiReferral
} from "./SideComponents";

export default function CompanyJobsPage() {
  const { company } = useParams();
  const companyName = decodeURIComponent(company);
  const { successToast } = useToastHelpers();
  const { currentTheme } = useTheme();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [activeItems, setActiveItems] = useState(0);
  const ITEMS_PER_PAGE = 10;

  const [keyword, setKeyword] = useState("");
  const [selectedType, setSelectedType] = useState("All");
  const [selectedExperience, setSelectedExperience] = useState("All");
  const [selectedLocation, setSelectedLocation] = useState("All");

  const locations = useMemo(() => {
    const uniqueLocations = new Set(["All"]);
    jobs.forEach((job) => {
      if (Array.isArray(job.locations)) {
        job.locations.forEach((loc) => {
          if (loc && loc.trim() !== "") uniqueLocations.add(loc.trim());
        });
      }
    });
    return Array.from(uniqueLocations);
  }, [jobs]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(totalItems / ITEMS_PER_PAGE);
  }, [totalItems]);

  // Load jobs
  const loadJobs = useCallback(async (newPage = 1) => {
    setLoading(true);

    const params = {
      page: newPage,
      size: ITEMS_PER_PAGE,
      company: company ? company.trim() : "Infosys",
      type: selectedType !== "All" ? selectedType : "",
      location: selectedLocation !== "All" ? selectedLocation : "",
      experience: selectedExperience !== "All" ? selectedExperience : "",
      keyword: keyword || "",
    };

    const result = await fetchAllJobs(params);

    if (result) {
      const jobsList = Array.isArray(result?.jobs) ? result.jobs : [];
      setJobs(jobsList);
      setTotalItems(result?.total || 0);
      setActiveItems(result?.active_jobs || 0);
      setPage(newPage);

      // Determine if last page
      const calculatedTotalPages = Math.ceil((result?.total || 0) / ITEMS_PER_PAGE);
      setIsLastPage(newPage >= calculatedTotalPages || jobsList.length === 0);
    } else {
      successToast("No more/matching jobs found");
      setIsLastPage(true);
    }

    setLoading(false);
  }, [company, keyword, selectedExperience, selectedLocation, selectedType, successToast]);

  useEffect(() => {
    loadJobs(1);
  }, [loadJobs]);

  const description = totalItems
    ? `Browse ${totalItems}+ open positions at ${companyName}. Find jobs for React, Java, Full Stack, internships, and referrals.`
    : `Find latest job openings at ${companyName} on Code Arena.`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SeoMeta
        title={`${companyName} Jobs | Code Arena`}
        description={description}
        url={`https://www.code-arena.in/companies/${companyName}`}
        image={jobs[0]?.companyLogo || "/logo192.png"}
      />

      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${currentTheme.colors.gradient} text-white`}>
        <div className="ca-container py-10 sm:py-14">
          <div className="flex items-center gap-4">
            {jobs[0]?.companyLogo && (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white shadow-xl p-2 flex-shrink-0">
                <img
                  src={jobs[0].companyLogo}
                  alt={`${companyName} logo`}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-2">
                {companyName} Jobs
              </h1>
              <p className="text-white/80 text-lg">
                Explore all open positions at {companyName}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="ca-container py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters */}
          <div className="w-full lg:w-1/4">
            <div className="sticky top-4">
              <JobFilters
                locations={locations}
                keyword={keyword}
                selectedCompany={company}
                setKeyword={setKeyword}
                selectedType={selectedType}
                setSelectedType={setSelectedType}
                selectedExperience={selectedExperience}
                setSelectedExperience={setSelectedExperience}
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
              />
            </div>
          </div>

          {/* Jobs */}
          <div className="w-full lg:w-2/4 flex flex-col">
            {loading ? (
              <div className="py-4">
                <SkeletonJobList count={5} />
              </div>
            ) : (
              <div className="md:max-h-[200vh] md:overflow-y-auto md:overflow-x-hidden hide-scrollbar">
                <JobList
                  jobs={jobs}
                  currentPage={page}
                  setCurrentPage={loadJobs}
                  isLastPage={isLastPage}
                />
              </div>
            )}

            {/* Pagination */}
            <div className="mt-6">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                isLastPage={isLastPage}
                onPageChange={loadJobs}
                showInfo={true}
              />
            </div>
          </div>

          {/* Right - Ads / Promotions */}
          <div className="w-full lg:w-1/4 space-y-6">
            <CapgeminiReferral />
          </div>
        </div>
      </div>
    </div>
  );
}
